import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BackLink, PageHeader } from '@/layouts/AppLayout';

const schema = z.object({
  email: z.string().email('Valid email required'),
  fullName: z.string().min(2, 'At least 2 characters'),
  role: z.enum(['super_admin', 'admin']),
  password: z
    .string()
    .min(10, 'At least 10 characters')
    .regex(/^(?=.*[A-Za-z])(?=.*\d)/, 'Must include a letter and a digit'),
});
type FormValues = z.infer<typeof schema>;

export function CreateUserPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { role: 'admin' },
  });

  const mutation = useMutation({
    mutationFn: (data: FormValues) => api.post('/users', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('User created');
      navigate('/settings/users');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to create user';
      setError('root', { message: msg });
    },
  });

  const role = watch('role');

  return (
    <div>
      <PageHeader title="New user" />
      <div className="p-6 max-w-lg">
        <BackLink to="/settings/users" label="Back to users" />
        <Card>
          <CardHeader>
            <CardTitle>User details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="fullName">Full name</Label>
                <Input id="fullName" placeholder="Jane Doe" {...register('fullName')} />
                {errors.fullName && <p className="text-xs text-danger-700">{errors.fullName.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="jane@diverse-hotels.com" {...register('email')} />
                {errors.email && <p className="text-xs text-danger-700">{errors.email.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={role} onValueChange={(v) => setValue('role', v as 'super_admin' | 'admin')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
                {errors.role && <p className="text-xs text-danger-700">{errors.role.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" placeholder="Min 10 chars, letter + digit" {...register('password')} />
                {errors.password && <p className="text-xs text-danger-700">{errors.password.message}</p>}
              </div>

              {errors.root && (
                <p className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{errors.root.message}</p>
              )}

              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Creating…' : 'Create user'}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate('/settings/users')}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
